import { useState, Dispatch, SetStateAction } from "react";
import { Spinner } from "@/components/ui/spinner";
import { ChevronDownIcon, CircleAlert, X, Send } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@clerk/react";
import { Link } from "react-router-dom";
import { CreatorAction } from "@/types/types";
import { useDashboardStore, useEveActionsStore } from "@/stores";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Textarea } from "@/components/ui/textarea";
import { ConfirmPopover } from "@/components/ui/confirm-popover";
import {
  Frame,
  FrameAction,
  FrameFooter,
  FrameHeader,
  FramePanel,
  FrameTitle,
} from "@/components/ui/frame";
import EmailThreadFrame from "./EmailThreadFrame";

interface HandoffActionProps {
  action: CreatorAction;
  emailThread: any[];
  threadLoading: boolean;
  setActionLoading: Dispatch<SetStateAction<boolean>>;
}

/**
 * Handoff task: Amy paused a creator with a question she couldn't answer.
 * The team's answer goes to the amy `resolve-handoff` route: the creator is
 * re-armed and the subagent owning her status replies in the thread with
 * it (Amy adds it to the knowledge base only when it is a reusable fact).
 * Dismiss takes the same route. Amy-v2 only by design — the legacy engine
 * retires in October 2026; its rows get the route's "not run by Amy"
 * refusal until then.
 */
export default function HandoffAction({
  action,
  emailThread,
  threadLoading,
  setActionLoading,
}: HandoffActionProps) {
  const [response, setResponse] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);

  const { getToken } = useAuth();
  const resolveHandoff = useEveActionsStore((s) => s.resolveHandoff);

  // Optimistic: the handoff row is gone once the route accepted the call
  // (the Tasks list re-fetches right after, via setActionLoading).
  const dropTask = () =>
    useDashboardStore.setState((state) => ({
      tasks: state.tasks.filter(
        (task) => !(task._id === action._id && task.actionType === "handoff")
      ),
    }));

  const resolve = async (resolution: "answer" | "dismiss"): Promise<boolean> => {
    const token = await getToken();
    if (!token) {
      toast.error("Please sign in again.");
      return false;
    }
    const ok = await resolveHandoff(
      action._id,
      resolution,
      resolution === "answer" ? response.trim() : undefined,
      token
    );
    if (ok) dropTask();
    return ok;
  };

  const handleDismissHandoff = async () => {
    if (isDismissing) return;
    setIsDismissing(true);
    try {
      await resolve("dismiss");
    } finally {
      setIsDismissing(false);
    }
  };

  const handleActionSubmit = async () => {
    if (!response.trim()) return;
    setSubmitting(true);
    setActionLoading(true);
    try {
      if (await resolve("answer")) setResponse("");
    } finally {
      setActionLoading(false);
      setSubmitting(false);
    }
  };

  // The row carries the handoff facts as fields; the "===" message is only
  // a fallback (it is null whenever either half is missing).
  const parts = (action.message ?? "").split("===");
  const unansweredQuestion =
    action.unanswered_question?.trim() ||
    parts[0]?.replace("Unanswered Question: ", "").trim() ||
    "";
  const debugInfo =
    action.debug_info?.trim() || parts[1]?.replace("Debug Info: ", "").trim() || "";
  const error = action.error?.trim() || "";

  return (
    <div className="flex-1 flex flex-col gap-4 animate-in fade-in-0 slide-in-from-top-2 duration-250">
      {error && (
        <Alert variant="destructive">
          <AlertTitle>
            <CircleAlert aria-hidden="true" />
            Error in creator's inbox:
          </AlertTitle>
          <AlertDescription>• {error}</AlertDescription>
        </Alert>
      )}

      <EmailThreadFrame
        action={action}
        emailThread={emailThread}
        threadLoading={threadLoading}
      />

      {/* The question and its answer are one task, after the thread that
          gives the context: the reply box sits right under the question. */}
      <Frame>
        <FrameHeader>
          <FrameTitle>Unanswered question</FrameTitle>
          <FrameAction>
            <ConfirmPopover
              title="Dismiss handoff?"
              description={
                <>
                  Clear the handoff without answering the creator. The question
                  and its details are lost.
                  <Badge variant="warning">Autopilot will turn ON</Badge>
                </>
              }
              confirmLabel={<><X /> Dismiss Handoff</>}
              confirmVariant="warning"
              onConfirm={handleDismissHandoff}
              disabled={isDismissing}
            >
              <Button variant="warning-ghost" size="sm" disabled={isDismissing}>
                <X />
                Dismiss
              </Button>
            </ConfirmPopover>
          </FrameAction>
        </FrameHeader>
        <FramePanel className="p-0 focus-within:border-warning focus-within:ring-3 focus-within:ring-warning/15">
          <p className="border-b px-4 py-3 text-sm whitespace-pre-wrap">
            {unansweredQuestion || "Amy didn't record the question."}
          </p>
          <div className="relative">
            <Textarea
              placeholder="Your answer — Amy replies to the creator in this thread."
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              maxLength={4000}
              className="min-h-24 resize-none rounded-none border-0 bg-transparent px-4 pb-12 shadow-none focus-visible:ring-0 dark:bg-transparent"
            />
            <Button
              onClick={handleActionSubmit}
              disabled={!response.trim() || submitting}
              className="absolute bottom-2 right-2"
              variant="neutral"
              size="sm"
            >
              {submitting ? <Spinner /> : <Send />}
              Reply with Amy
            </Button>
          </div>
        </FramePanel>
        <Collapsible>
          <FrameFooter className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <span>
                Reusable answers also go to the{" "}
                <Link to="../settings/data" className="underline underline-offset-2 hover:text-foreground">
                  Knowledge Base
                </Link>
                .
              </span>
              {debugInfo && (
                <CollapsibleTrigger asChild>
                  <Button variant="muted-ghost" size="xs" className="group shrink-0">
                    Why Amy stopped
                    <ChevronDownIcon className="transition-transform group-data-[state=open]:rotate-180" />
                  </Button>
                </CollapsibleTrigger>
              )}
            </div>
            {debugInfo && (
              <CollapsibleContent className="whitespace-pre-wrap">{debugInfo}</CollapsibleContent>
            )}
          </FrameFooter>
        </Collapsible>
      </Frame>
    </div>
  );
}
